/**
 * MultiSelect Component
 *
 * A generic, reusable multi-select component that works seamlessly with React Hook Form.
 *
 * Features:
 * - Generic TypeScript support
 * - React Hook Form integration via forwardRef
 * - Custom tag creation
 * - Search/filter functionality
 * - Keyboard navigation
 * - Accessibility support
 * - Error state handling
 * - Disabled state support
 * - Individual option disabled state
 * - Select All toggle
 * - Custom trigger support
 * - Group heading support
 *
 * Usage with React Hook Form:
 *
 * Method 1 - Using MultiSelectField (Recommended):
 * ```tsx
 * <FormField
 *   control={form.control}
 *   name="tags"
 *   render={({ field }) => (
 *     <MultiSelectField
 *       title="Select tags"
 *       options={tagOptions} // Array of { label: string, value: string, description?: string }
 *       value={field.value?.map(tag => tag.value) || []} // Extract string values
 *       onChange={(selectedValues) => {
 *         const selectedTags = selectedValues.map(value =>
 *           tagOptions.find(option => option.value === value)
 *         ).filter(Boolean);
 *         field.onChange(selectedTags);
 *       }}
 *       onCreateNew={handleCreateNewTag}
 *       createNewText="Create new tag"
 *     />
 *   )}
 * />
 * ```
 *
 * Method 2 - Using MultiSelect directly:
 * ```tsx
 * <FormField
 *   control={form.control}
 *   name="tags"
 *   render={({ field }) => (
 *     <MultiSelect
 *       title="Select tags"
 *       options={tagOptions} // Array of { label: string, value: string, description?: string }
 *       selectedValues={field.value?.map(tag => tag.value) || []} // Extract string values
 *       onSelectionChange={(selectedValues) => {
 *         const selectedTags = selectedValues.map(value =>
 *           tagOptions.find(option => option.value === value)
 *         ).filter(Boolean);
 *         field.onChange(selectedTags);
 *       }}
 *       onCreateNew={handleCreateNewTag}
 *     />
 *   )}
 * />
 * ```
 */

import * as React from 'react';

import { CheckIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

/**
 * A modal Dialog locks scrolling with `react-remove-scroll`, which cancels every wheel event from
 * a listener on `document`. The popover is portalled outside the dialog, so the list counted as
 * outside and only answered the keyboard. Stopping the event before `document` sees it leaves the
 * browser free to scroll the list natively.
 */
const keepWheelOffDocument = (event: React.WheelEvent<HTMLDivElement>) => event.stopPropagation();

const matchRank = (option: MultiSelectOption, search: string) => {
  const term = search.toLowerCase();
  const label = option.label.toLowerCase();
  const value = option.value.toLowerCase();
  if (label === term || value === term) {
    return 0;
  }
  if (label.startsWith(term) || value.startsWith(term)) {
    return 1;
  }
  return 2;
};

export interface MultiSelectOption {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
  description?: string;
  group?: string;
}

export interface MultiSelectProps {
  title?: string;
  options: MultiSelectOption[];
  selectedValues?: string[];
  onSelectionChange?: (selectedValues: string[]) => void;
  placeholder?: string;
  allowCreateNew?: boolean;
  onCreateNew?: (newValue: string) => void;
  createNewText?: string | ((searchValue: string) => string);
  noResultsText?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  showSelectAll?: boolean;
  selectAllLabel?: string;
  groupHeading?: string;
  trigger?: React.ReactNode;
  sortOptions?: boolean;
  popoverClassName?: string;
  popoverAlign?: 'start' | 'center' | 'end';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onFocusOutside?: (event: Event) => void;
  emptyState?: React.ReactNode;
}

export const MultiSelect = ({
  title,
  options,
  selectedValues: externalSelectedValues = [],
  onSelectionChange,
  placeholder,
  onCreateNew,
  createNewText,
  noResultsText,
  disabled = false,
  error = false,
  showSelectAll = false,
  selectAllLabel,
  groupHeading,
  trigger,
  sortOptions = true,
  popoverClassName,
  popoverAlign = 'start',
  open,
  onOpenChange,
  onFocusOutside,
  emptyState,
  ref,
}: MultiSelectProps & { ref?: React.Ref<HTMLButtonElement> }) => {
  const [internalSelectedValues, setInternalSelectedValues] =
    React.useState<string[]>(externalSelectedValues);
  const [searchValue, setSearchValue] = React.useState('');
  const { t } = useTranslation();

  // Use external values if onSelectionChange is provided (controlled mode),
  // otherwise use internal state (uncontrolled mode)
  const selectedValues = React.useMemo(
    () => new Set(onSelectionChange ? externalSelectedValues : internalSelectedValues),
    [externalSelectedValues, internalSelectedValues, onSelectionChange]
  );

  const allSelected = options.length > 0 && options.every((o) => selectedValues.has(o.value));

  // Filter options based on search
  const filteredOptions = React.useMemo(() => {
    if (!searchValue) {
      return options;
    }
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
        option.value.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [options, searchValue]);

  // Check if we should show "Create new" option
  const shouldShowCreateNew = React.useMemo(() => {
    return (
      onCreateNew &&
      searchValue.trim().length > 0 &&
      !options.some(
        (option) =>
          option.label.toLowerCase() === searchValue.toLowerCase() ||
          option.value.toLowerCase() === searchValue.toLowerCase()
      )
    );
  }, [onCreateNew, searchValue, options]);

  // Handle creating a new tag
  const handleCreateNewTag = React.useCallback(() => {
    if (onCreateNew && searchValue.trim()) {
      onCreateNew(searchValue.trim());
      setSearchValue(''); // Clear search after creating
    }
  }, [onCreateNew, searchValue]);

  const resolvedCreateNewText =
    typeof createNewText === 'function' ? createNewText(searchValue) : createNewText;

  const updateSelection = React.useCallback(
    (newValues: string[]) => {
      if (onSelectionChange) {
        onSelectionChange(newValues);
      } else {
        setInternalSelectedValues(newValues);
      }
    },
    [onSelectionChange]
  );

  const handleSelectAll = React.useCallback(() => {
    if (allSelected) {
      updateSelection([]);
    } else {
      updateSelection(options.map((o) => o.value));
    }
  }, [allSelected, options, updateSelection]);

  const visibleOptions = React.useMemo(() => {
    const ordered = sortOptions
      ? [...filteredOptions].sort((a, b) => a.label.localeCompare(b.label))
      : filteredOptions;
    if (!searchValue) {
      return ordered;
    }
    return [...ordered].sort((a, b) => matchRank(a, searchValue) - matchRank(b, searchValue));
  }, [filteredOptions, searchValue, sortOptions]);

  // A group appears where its first option does, so the caller's order decides the sections.
  const groupedOptions = React.useMemo(() => {
    const buckets = new Map<string | undefined, MultiSelectOption[]>();
    visibleOptions.forEach((option) => {
      buckets.set(option.group, [...(buckets.get(option.group) ?? []), option]);
    });

    return [...buckets];
  }, [visibleOptions]);

  const renderOption = (option: MultiSelectOption) => {
    const isSelected = selectedValues.has(option.value);
    const isDisabled = option.disabled;
    return (
      <CommandItem
        key={option.value}
        className="cursor-pointer data-[disabled=true]:cursor-default"
        disabled={isDisabled}
        onSelect={() => {
          if (isDisabled) {
            return;
          } // Prevent selection of disabled items

          const newSelectedValues = new Set(selectedValues);
          if (isSelected) {
            newSelectedValues.delete(option.value);
          } else {
            newSelectedValues.add(option.value);
          }
          updateSelection(Array.from(newSelectedValues));
        }}
      >
        <div
          className={cn(
            'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-input',
            { 'bg-subtle opacity-50': isDisabled },
            { 'border-selected bg-selected text-primary-foreground': isSelected && !isDisabled },
            { 'opacity-50 [&_svg]:invisible': !isDisabled && !isSelected },
            option.description && 'mt-0.5 self-start'
          )}
        >
          <CheckIcon className={cn({ 'text-primary-foreground': isSelected && !isDisabled })} />
        </div>
        {option.icon && (
          <option.icon
            className={cn(
              'mr-2 h-4 w-4 shrink-0',
              isDisabled ? 'text-muted-foreground opacity-50' : 'text-muted-foreground',
              option.description && 'mt-0.5 self-start'
            )}
          />
        )}
        <div className="flex min-w-0 flex-col">
          <span
            className={cn('truncate', isDisabled && 'text-muted-foreground opacity-50')}
            title={option.label}
          >
            {option.label}
          </span>
          {option.description && (
            <span
              className={cn('truncate text-xs text-muted-foreground', isDisabled && 'opacity-50')}
              title={option.description}
            >
              {option.description}
            </span>
          )}
        </div>
      </CommandItem>
    );
  };

  const defaultTrigger = (
    <Button
      ref={ref}
      variant="outline-neutral"
      size="sm"
      className={cn(
        'h-8 max-w-full min-w-0 justify-start',
        error && 'border-destructive',
        disabled && 'cursor-not-allowed opacity-50'
      )}
      disabled={disabled}
    >
      <PlusIcon />
      {title}
      {selectedValues?.size > 0 && (
        <>
          <Separator orientation="vertical" className="mx-2 h-4" />
          <div className="flex min-w-0 flex-1 flex-wrap gap-1 overflow-hidden">
            {selectedValues.size > 2 ? (
              <Badge type="secondary" className="rounded-sm px-1 font-normal">
                {selectedValues.size} selected
              </Badge>
            ) : (
              options
                .filter((option) => selectedValues.has(option.value))
                .map((option) => (
                  <Badge
                    type="secondary"
                    key={option.value}
                    className="max-w-30 justify-start rounded-sm px-1 font-normal"
                    title={option.label}
                  >
                    {option.label}
                  </Badge>
                ))
            )}
          </div>
        </>
      )}
    </Button>
  );

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger || defaultTrigger}</PopoverTrigger>
      <PopoverContent
        className={cn(
          'z-1300 min-w-50 p-0',
          trigger ? 'w-(--radix-popover-trigger-width)' : 'max-w-75',
          popoverClassName
        )}
        align={popoverAlign}
        onFocusOutside={onFocusOutside}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder || title}
            value={searchValue}
            onValueChange={setSearchValue}
            autoFocus
          />
          <CommandList
            onWheelCapture={keepWheelOffDocument}
            className="max-h-[calc(var(--radix-popover-content-available-height)-3rem)]"
          >
            {!shouldShowCreateNew && (
              <CommandEmpty className={emptyState ? 'p-0' : 'py-2 text-center'}>
                {emptyState ?? (noResultsText || t('common.no_results'))}
              </CommandEmpty>
            )}

            {showSelectAll && !searchValue && (
              <CommandGroup className="pb-0">
                <CommandItem className="cursor-pointer" onSelect={handleSelectAll}>
                  <div
                    className={cn(
                      'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-input',
                      allSelected && 'border-selected bg-selected text-primary-foreground',
                      !allSelected && 'opacity-50 [&_svg]:invisible'
                    )}
                  >
                    <CheckIcon className="text-primary-foreground" />
                  </div>
                  <span className="font-medium">
                    {selectAllLabel || `${t('common.all')} (${options.length})`}
                  </span>
                </CommandItem>
              </CommandGroup>
            )}
            {groupedOptions.map(([group, groupOptions], index) => (
              <CommandGroup
                key={group ?? ''}
                heading={group ?? groupHeading}
                className={cn(showSelectAll && index === 0 && !(group ?? groupHeading) && 'pt-0')}
              >
                {groupOptions.map(renderOption)}
              </CommandGroup>
            ))}
            {shouldShowCreateNew && resolvedCreateNewText && (
              <CommandGroup forceMount className="border-t">
                <CommandItem className="cursor-pointer" forceMount onSelect={handleCreateNewTag}>
                  <PlusIcon />
                  <span className="overflow-hidden text-ellipsis">{resolvedCreateNewText}</span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/**
 * React Hook Form compatible wrapper for MultiSelect
 * This component integrates seamlessly with react-hook-form's Controller
 */
export interface MultiSelectFieldProps extends Omit<
  MultiSelectProps,
  'selectedValues' | 'onSelectionChange'
> {
  value?: string[];
  onChange?: (value: string[]) => void;
  name?: string;
}

export const MultiSelectField = ({
  value = [],
  onChange,
  ref,
  ...props
}: MultiSelectFieldProps & { ref?: React.Ref<HTMLButtonElement> }) => {
  return <MultiSelect {...props} ref={ref} selectedValues={value} onSelectionChange={onChange} />;
};
